/**
 * threadArchiveRequest — the browser's view of a request to archive a pinned
 * thread for everyone (catalog v18; Miles, 2026-09-23: "when a user pins a
 * thread of any kind, that individual … needs to approve an archive of that
 * thread if any other human or Prest0n initiates an archive of this").
 *
 * Pure and Firebase-free. Three readers share it, so they can never disagree
 * about what a request says:
 *   - the request listener, which reads the flat stamp fields the server writes
 *     on each member's own copy of the thread (`archiveRequest*`);
 *   - the membership write client, which reads the view the server returns with
 *     an answer (the asker's bar comes straight from that answer);
 *   - the bar, which asks who this viewer is to the request: a person asked to
 *     answer, the asker, or someone else in the thread.
 *
 * THE SERVER'S RECORD DECIDES. A person may edit their own copy of a thread, so
 * nothing here authorizes anything: a forged stamp changes only that person's
 * own view, and every answer is re-checked by the server against its record.
 */

export type ThreadArchiveAnswer = 'accept' | 'decline' | 'withdraw';

export interface ThreadArchiveApprover {
  uid: string;
  /** '' when the server could not resolve a name. */
  name: string;
  accepted: boolean;
}

/** One open request to archive a thread for everyone, as this browser knows it. */
export interface ThreadArchiveRequest {
  threadId: string;
  requestId: string;
  /** Who asked; '' when Prest0n asked on its own (a routine, a check-in). */
  requestedBy: string;
  requestedByName: string;
  /** True when the ask came through Prest0n, for a person or on its own. */
  viaPrest0n: boolean;
  /** When an unanswered request counts as declined (ms since the epoch). */
  expiresAtMs: number;
  /** The people who pinned the thread and must accept, in the server's order. */
  approvers: ThreadArchiveApprover[];
}

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
const texts = (value: unknown): string[] => (Array.isArray(value) ? value.map(text) : []);

function finalize(input: Omit<ThreadArchiveRequest, 'approvers'> & { approvers: ThreadArchiveApprover[] }): ThreadArchiveRequest | null {
  if (!input.threadId || !input.requestId) return null;
  if (!Number.isFinite(input.expiresAtMs) || input.expiresAtMs <= 0) return null;
  const seen = new Set<string>();
  const approvers = input.approvers.filter((a) => {
    if (!a.uid || seen.has(a.uid)) return false;
    seen.add(a.uid);
    return true;
  });
  // A request with nobody left to answer is decided (the server completes it at
  // once), so there is nothing for anyone to see.
  if (approvers.length === 0) return null;
  return { ...input, approvers };
}

/**
 * The request a member's own copy of the thread carries, or null. The server
 * writes the stamps (threadArchiveRequests.ts `archiveRequestStampFields`) and
 * deletes them when the request ends; an archived copy never carries a live one.
 */
export function parseArchiveRequestStamps(threadId: string, raw: unknown): ThreadArchiveRequest | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  if (data.archiveRequestOpen !== true || data.archived === true) return null;
  const uids = texts(data.archiveRequestApprovers);
  const names = texts(data.archiveRequestApproverNames);
  const accepted = new Set(texts(data.archiveRequestAccepted).filter(Boolean));
  return finalize({
    threadId: text(threadId),
    requestId: text(data.archiveRequestId),
    requestedBy: text(data.archiveRequestBy),
    requestedByName: text(data.archiveRequestByName),
    viaPrest0n: data.archiveRequestViaPrest0n === true,
    expiresAtMs: Number(data.archiveRequestExpiresAt),
    // Names ride a parallel array; a missing one is '' and the copy says "someone".
    approvers: uids.map((uid, i) => ({ uid, name: names[i] || '', accepted: accepted.has(uid) })),
  });
}

/**
 * The request the server returned with an answer (`archiveRequest` on the
 * membership write's response — `toArchiveRequestView`), or null.
 */
export function parseArchiveRequestView(threadId: string, raw: unknown): ThreadArchiveRequest | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  const approvers = Array.isArray(data.approvers)
    ? data.approvers.map((entry) => {
      const e = (entry && typeof entry === 'object' ? entry : {}) as Record<string, unknown>;
      return { uid: text(e.uid), name: text(e.name), accepted: e.accepted === true };
    })
    : [];
  return finalize({
    threadId: text(threadId),
    requestId: text(data.requestId),
    requestedBy: text(data.requestedBy),
    requestedByName: text(data.requestedByName),
    viaPrest0n: data.viaPrest0n === true,
    expiresAtMs: Number(data.expiresAt),
    approvers,
  });
}

/**
 * Who this viewer is to the request:
 *   - 'approver': asked to answer, not yet answered — Decline / Accept;
 *   - 'approver-accepted': accepted, others still to answer — may still Decline;
 *   - 'asker': the person who asked — Withdraw;
 *   - 'observer': anyone else in the thread — a note, no buttons.
 */
export type ThreadArchiveRequestRole = 'approver' | 'approver-accepted' | 'asker' | 'observer';

export interface ThreadArchiveRequestSeat {
  role: ThreadArchiveRequestRole;
  /** True when Prest0n asked on its own; the asker is then named "Prest0n". */
  askedByPrest0n: boolean;
  /** The asker's name ('' when unknown, or when Prest0n asked on its own). */
  askerName: string;
  /** Everyone else still to answer, in the server's order ('' = no name). */
  waitingOn: string[];
  /** Everyone else who has already accepted ('' = no name). */
  acceptedBy: string[];
  expiresAtMs: number;
}

/**
 * The viewer's seat at a request, or null when there is nothing to show — no
 * request, or one whose seven days have run out. The server declines an expired
 * request on its next pass; the bar must not offer an answer the server will
 * refuse in the meantime.
 */
export function archiveRequestSeatFor(
  request: ThreadArchiveRequest | null | undefined,
  viewerUid: string | null | undefined,
  nowMs: number,
): ThreadArchiveRequestSeat | null {
  if (!request || !(nowMs < request.expiresAtMs)) return null;
  const viewer = text(viewerUid);
  const mine = viewer ? request.approvers.find((a) => a.uid === viewer) : undefined;
  const role: ThreadArchiveRequestRole = mine
    ? (mine.accepted ? 'approver-accepted' : 'approver')
    : (viewer && request.requestedBy && viewer === request.requestedBy ? 'asker' : 'observer');
  const others = request.approvers.filter((a) => a.uid !== viewer);
  return {
    role,
    askedByPrest0n: !request.requestedBy,
    askerName: request.requestedBy ? request.requestedByName : '',
    waitingOn: others.filter((a) => !a.accepted).map((a) => a.name),
    acceptedBy: others.filter((a) => a.accepted).map((a) => a.name),
    expiresAtMs: request.expiresAtMs,
  };
}

/**
 * Two views of the same request are the same when nothing a person could see
 * differs. The listener hands a fresh object on every snapshot; comparing by
 * value keeps the bar from re-rendering (and re-announcing) on unrelated writes
 * to the same thread doc.
 */
export function sameArchiveRequest(a: ThreadArchiveRequest | null | undefined, b: ThreadArchiveRequest | null | undefined): boolean {
  if (!a || !b) return !a && !b;
  if (a.threadId !== b.threadId || a.requestId !== b.requestId || a.requestedBy !== b.requestedBy
    || a.requestedByName !== b.requestedByName || a.viaPrest0n !== b.viaPrest0n
    || a.expiresAtMs !== b.expiresAtMs || a.approvers.length !== b.approvers.length) {
    return false;
  }
  return a.approvers.every((x, i) => {
    const y = b.approvers[i];
    return x.uid === y.uid && x.name === y.name && x.accepted === y.accepted;
  });
}

/** How long a request this tab just learned from an answer outranks the listener (ms). */
export const ARCHIVE_ANSWER_OVERRIDE_MS = 8_000;

/**
 * What an answer just told this tab about one thread's request: the request as
 * it now stands, or that it ended (`request: null`, naming the one that ended).
 */
export interface ArchiveRequestOverride {
  request: ThreadArchiveRequest | null;
  /** When `request` is null: the request that ended. */
  endedRequestId?: string;
  atMs: number;
}

/**
 * The requests to show, by thread: what the listener says, with what this tab's
 * own answers just learned laid over it for a few seconds — the asker's bar
 * appears the moment the server answers, and a Decline takes the bar down at
 * once — until the listener catches up. The listener stays the authority for
 * anything the answer did not know: a DIFFERENT request on the same thread is
 * news only it has, so it wins.
 */
export function mergeArchiveRequests(
  listened: readonly ThreadArchiveRequest[],
  overrides: Readonly<Record<string, ArchiveRequestOverride>>,
  nowMs: number,
): Record<string, ThreadArchiveRequest> {
  const out: Record<string, ThreadArchiveRequest> = {};
  for (const request of listened) out[request.threadId] = request;
  for (const [threadId, override] of Object.entries(overrides)) {
    if (!(nowMs - override.atMs < ARCHIVE_ANSWER_OVERRIDE_MS)) continue;
    const heard = out[threadId];
    if (override.request) {
      if (heard && heard.requestId !== override.request.requestId) continue;
      out[threadId] = override.request;
      continue;
    }
    if (heard && override.endedRequestId && heard.requestId !== override.endedRequestId) continue;
    delete out[threadId];
  }
  return out;
}

/** Two listener deliveries that show the same requests (by value, order included). */
export function sameArchiveRequestList(a: readonly ThreadArchiveRequest[], b: readonly ThreadArchiveRequest[]): boolean {
  return a.length === b.length && a.every((request, i) => sameArchiveRequest(request, b[i]));
}
